import { useState, useEffect } from 'react';
import { searchHelpArticles } from '../api/GlobalApi';

function HelpSearchPage() {
  const [searchQuery, setSearchQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('all');

  useEffect(() => {
    // Get query from URL
    const urlParams = new URLSearchParams(window.location.search);
    const query = urlParams.get('q');
    if (query) {
      setSearchQuery(query);
      performSearch(query);
    }
  }, []);

  const performSearch = async (query) => {
    if (!query.trim()) return;

    try {
      setLoading(true);
      setSearched(true);
      const response = await searchHelpArticles(query);
      setResults(response.results || []);
    } catch (error) {
      console.error('Error searching articles:', error);
      setResults([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    if (searchQuery.trim()) {
      window.history.pushState({}, '', `/help/search?q=${encodeURIComponent(searchQuery)}`);
      performSearch(searchQuery);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  // Get unique categories from results
  const categories = ['all', ...new Set(results.map(article => article.category?.name).filter(Boolean))];

  // Filter results by selected category
  const filteredResults = selectedCategory === 'all'
    ? results
    : results.filter(article => article.category?.name === selectedCategory);

  // Highlight search term in text
  const highlightText = (text, query) => {
    if (!text || !query) return text;
    const parts = text.split(new RegExp(`(${query})`, 'gi'));
    return parts.map((part, i) =>
      part.toLowerCase() === query.toLowerCase()
        ? `<mark key="${i}">${part}</mark>`
        : part
    ).join('');
  };

  return (
    <main className="main-content">
      {/* Header */}
      <div style={styles.header}>
        <div style={styles.headerContent}>
          <h1 style={styles.headerTitle}>Search Help Articles</h1>
          <p style={styles.headerSubtitle}>
            {searched && searchQuery
              ? `Results for "${searchQuery}"`
              : 'Find answers to your questions'}
          </p>
        </div>
      </div>

      <div style={styles.container}>
        {/* Search Box */}
        <div style={styles.searchSection}>
          <div style={styles.searchBox}>
            <svg style={styles.searchIcon} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            <input
              type="text"
              placeholder="Search for help articles..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyPress={handleKeyPress}
              style={styles.searchInput}
              autoFocus
            />
            {searchQuery && (
              <button onClick={() => setSearchQuery('')} style={styles.clearBtn}>
                <svg width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
                  <path d="M4.646 4.646a.5.5 0 0 1 .708 0L8 7.293l2.646-2.647a.5.5 0 0 1 .708.708L8.707 8l2.647 2.646a.5.5 0 0 1-.708.708L8 8.707l-2.646 2.647a.5.5 0 0 1-.708-.708L7.293 8 4.646 5.354a.5.5 0 0 1 0-.708z"/>
                </svg>
              </button>
            )}
            <button onClick={handleSearch} style={styles.searchBtn}>
              Search
            </button>
          </div>
        </div>

        {loading ? (
          <div style={styles.loadingContainer}>
            <div style={styles.spinner}></div>
            <p style={styles.loadingText}>Searching...</p>
          </div>
        ) : searched ? (
          <>
            {/* Results Header */}
            <div style={styles.resultsHeader}>
              <div style={styles.resultsCount}>
                {filteredResults.length > 0 ? (
                  <>
                    Found <strong>{filteredResults.length}</strong> {filteredResults.length === 1 ? 'article' : 'articles'}
                  </>
                ) : (
                  <>No articles found</>
                )}
              </div>

              {/* Category Filter */}
              {results.length > 0 && categories.length > 1 && (
                <div style={styles.filterSection}>
                  <span style={styles.filterLabel}>Filter by:</span>
                  <div style={styles.categoryTabs}>
                    {categories.map((category) => (
                      <button
                        key={category}
                        onClick={() => setSelectedCategory(category)}
                        style={{
                          ...styles.categoryTab,
                          ...(selectedCategory === category ? styles.categoryTabActive : {})
                        }}
                      >
                        {category === 'all' ? 'All Categories' : category}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Results List */}
            {filteredResults.length > 0 ? (
              <div style={styles.resultsList}>
                {filteredResults.map((article) => (
                  <a
                    key={article.id}
                    href={`/help/article/${article.slug}`}
                    style={styles.resultCard}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = 'translateY(-2px)';
                      e.currentTarget.style.boxShadow = '0 6px 20px rgba(0,0,0,0.12)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.08)';
                    }}
                  >
                    <div style={styles.resultHeader}>
                      <span style={styles.categoryBadge}>{article.category?.name}</span>
                      <div style={styles.resultMeta}>
                        <svg width="14" height="14" fill="currentColor" viewBox="0 0 16 16">
                          <path d="M16 8s-3-5.5-8-5.5S0 8 0 8s3 5.5 8 5.5S16 8 16 8zM1.173 8a13.133 13.133 0 0 1 1.66-2.043C4.12 4.668 5.88 3.5 8 3.5c2.12 0 3.879 1.168 5.168 2.457A13.133 13.133 0 0 1 14.828 8c-.058.087-.122.183-.195.288-.335.48-.83 1.12-1.465 1.755C11.879 11.332 10.119 12.5 8 12.5c-2.12 0-3.879-1.168-5.168-2.457A13.134 13.134 0 0 1 1.172 8z"/>
                          <path d="M8 5.5a2.5 2.5 0 1 0 0 5 2.5 2.5 0 0 0 0-5zM4.5 8a3.5 3.5 0 1 1 7 0 3.5 3.5 0 0 1-7 0z"/>
                        </svg>
                        <span>{article.view_count} views</span>
                      </div>
                    </div>
                    <h3 style={styles.resultTitle}>{article.title}</h3>
                    {article.excerpt && (
                      <p style={styles.resultExcerpt}>{article.excerpt}</p>
                    )}
                    <div style={styles.resultFooter}>
                      <span style={styles.readMore}>
                        Read article
                        <svg width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
                          <path fillRule="evenodd" d="M1 8a.5.5 0 0 1 .5-.5h11.793l-3.147-3.146a.5.5 0 0 1 .708-.708l4 4a.5.5 0 0 1 0 .708l-4 4a.5.5 0 0 1-.708-.708L13.293 8.5H1.5A.5.5 0 0 1 1 8z"/>
                        </svg>
                      </span>
                    </div>
                  </a>
                ))}
              </div>
            ) : (
              <div style={styles.emptyState}>
                <svg style={styles.emptyIcon} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <h3 style={styles.emptyTitle}>No Results Found</h3>
                <p style={styles.emptyText}>
                  We couldn't find any articles matching "{searchQuery}"
                </p>
                <div style={styles.suggestions}>
                  <h4 style={styles.suggestionsTitle}>Try:</h4>
                  <ul style={styles.suggestionsList}>
                    <li>Using different keywords</li>
                    <li>Checking your spelling</li>
                    <li>Using more general terms</li>
                    <li>Browsing our categories</li>
                  </ul>
                </div>
                <button onClick={() => window.location.href = '/help'} style={styles.browseBtn}>
                  Browse All Categories
                </button>
              </div>
            )}
          </>
        ) : (
          <div style={styles.initialState}>
            <svg style={styles.initialIcon} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            <h3 style={styles.initialTitle}>Start your search</h3>
            <p style={styles.initialText}>
              Enter keywords to find help articles, guides, and documentation
            </p>
          </div>
        )}
      </div>

      {/* Popular Searches */}
      {!searched && (
        <div style={styles.popularSection}>
          <h3 style={styles.popularTitle}>Popular Searches</h3>
          <div style={styles.popularTags}>
            {['Getting Started', 'Account Settings', 'Billing', 'Technical Support', 'Privacy'].map((tag) => (
              <button
                key={tag}
                onClick={() => {
                  setSearchQuery(tag);
                  performSearch(tag);
                }}
                style={styles.popularTag}
              >
                {tag}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Back Link */}
      <div style={styles.backSection}>
        <a href="/help" style={styles.backLink}>
          <svg width="20" height="20" fill="currentColor" viewBox="0 0 16 16">
            <path fillRule="evenodd" d="M15 8a.5.5 0 0 0-.5-.5H2.707l3.147-3.146a.5.5 0 1 0-.708-.708l-4 4a.5.5 0 0 0 0 .708l4 4a.5.5 0 0 0 .708-.708L2.707 8.5H14.5A.5.5 0 0 0 15 8z"/>
          </svg>
          Back to Help Center
        </a>
      </div>
    </main>
  );
}

const styles = {
  header: {
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    padding: '60px 20px',
    textAlign: 'center',
    color: 'white',
    marginBottom: '40px',
  },
  headerContent: {
    maxWidth: '1200px',
    margin: '0 auto',
  },
  headerTitle: {
    fontSize: '40px',
    fontWeight: '600',
    marginBottom: '12px',
  },
  headerSubtitle: {
    fontSize: '18px',
    opacity: 0.95,
  },
  container: {
    maxWidth: '1200px',
    margin: '0 auto',
    padding: '0 20px 60px',
  },
  searchSection: {
    marginBottom: '40px',
  },
  searchBox: {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  searchIcon: {
    position: 'absolute',
    left: '20px',
    width: '20px',
    height: '20px',
    color: '#5f6368',
    pointerEvents: 'none',
  },
  searchInput: {
    flex: 1,
    height: '56px',
    padding: '0 140px 0 56px',
    fontSize: '16px',
    border: '1px solid #dadce0',
    borderRadius: '28px',
    outline: 'none',
    transition: 'all 0.2s',
  },
  clearBtn: {
    position: 'absolute',
    right: '140px',
    width: '28px',
    height: '28px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    border: 'none',
    background: 'none',
    cursor: 'pointer',
    color: '#5f6368',
    borderRadius: '50%',
    transition: 'all 0.2s',
  },
  searchBtn: {
    position: 'absolute',
    right: '8px',
    height: '40px',
    padding: '0 32px',
    background: '#1a73e8',
    color: 'white',
    border: 'none',
    borderRadius: '20px',
    cursor: 'pointer',
    fontSize: '15px',
    fontWeight: '500',
    transition: 'all 0.2s',
  },
  loadingContainer: {
    textAlign: 'center',
    padding: '80px 20px',
  },
  spinner: {
    width: '50px',
    height: '50px',
    border: '6px solid #ddd',
    borderTop: '6px solid #3498db',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite',
    margin: '0 auto 16px',
  },
  loadingText: {
    fontSize: '16px',
    color: '#5f6368',
  },
  resultsHeader: {
    marginBottom: '32px',
  },
  resultsCount: {
    fontSize: '18px',
    color: '#202124',
    marginBottom: '20px',
  },
  filterSection: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    flexWrap: 'wrap',
  },
  filterLabel: {
    fontSize: '14px',
    color: '#5f6368',
    fontWeight: '500',
  },
  categoryTabs: {
    display: 'flex',
    gap: '8px',
    flexWrap: 'wrap',
  },
  categoryTab: {
    padding: '8px 16px',
    background: 'white',
    border: '1px solid #dadce0',
    borderRadius: '20px',
    color: '#5f6368',
    fontSize: '14px',
    fontWeight: '500',
    cursor: 'pointer',
    transition: 'all 0.2s',
  },
  categoryTabActive: {
    background: '#1a73e8',
    color: 'white',
    borderColor: '#1a73e8',
  },
  resultsList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
  },
  resultCard: {
    padding: '24px',
    background: 'white',
    border: '1px solid #e8eaed',
    borderRadius: '12px',
    textDecoration: 'none',
    transition: 'all 0.3s ease',
    display: 'block',
    boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
  },
  resultHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: '12px',
    gap: '16px',
  },
  categoryBadge: {
    display: 'inline-block',
    padding: '4px 12px',
    fontSize: '12px',
    fontWeight: '500',
    color: '#667eea',
    backgroundColor: '#f0f2ff',
    borderRadius: '12px',
  },
  resultMeta: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    fontSize: '13px',
    color: '#5f6368',
  },
  resultTitle: {
    fontSize: '20px',
    fontWeight: '500',
    color: '#202124',
    marginBottom: '8px',
    lineHeight: '1.4',
  },
  resultExcerpt: {
    fontSize: '15px',
    color: '#5f6368',
    lineHeight: '1.6',
    marginBottom: '16px',
  },
  resultFooter: {
    display: 'flex',
    justifyContent: 'flex-end',
  },
  readMore: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    color: '#1a73e8',
    fontSize: '14px',
    fontWeight: '500',
  },
  emptyState: {
    textAlign: 'center',
    padding: '80px 20px',
  },
  emptyIcon: {
    width: '80px',
    height: '80px',
    color: '#dadce0',
    margin: '0 auto 24px',
  },
  emptyTitle: {
    fontSize: '28px',
    fontWeight: '600',
    color: '#202124',
    marginBottom: '12px',
  },
  emptyText: {
    fontSize: '16px',
    color: '#5f6368',
    marginBottom: '32px',
  },
  suggestions: {
    maxWidth: '400px',
    margin: '0 auto 32px',
    textAlign: 'left',
  },
  suggestionsTitle: {
    fontSize: '16px',
    fontWeight: '500',
    color: '#202124',
    marginBottom: '12px',
  },
  suggestionsList: {
    listStyle: 'disc',
    paddingLeft: '20px',
    color: '#5f6368',
    fontSize: '14px',
    lineHeight: '2',
  },
  browseBtn: {
    padding: '12px 32px',
    background: '#1a73e8',
    color: 'white',
    border: 'none',
    borderRadius: '24px',
    cursor: 'pointer',
    fontSize: '15px',
    fontWeight: '500',
    transition: 'all 0.2s',
  },
  initialState: {
    textAlign: 'center',
    padding: '80px 20px',
  },
  initialIcon: {
    width: '80px',
    height: '80px',
    color: '#dadce0',
    margin: '0 auto 24px',
  },
  initialTitle: {
    fontSize: '28px',
    fontWeight: '600',
    color: '#202124',
    marginBottom: '12px',
  },
  initialText: {
    fontSize: '16px',
    color: '#5f6368',
  },
  popularSection: {
    maxWidth: '1200px',
    margin: '0 auto 60px',
    padding: '0 20px',
  },
  popularTitle: {
    fontSize: '20px',
    fontWeight: '500',
    color: '#202124',
    marginBottom: '16px',
  },
  popularTags: {
    display: 'flex',
    gap: '12px',
    flexWrap: 'wrap',
  },
  popularTag: {
    padding: '10px 20px',
    background: 'white',
    border: '1px solid #dadce0',
    borderRadius: '20px',
    color: '#5f6368',
    fontSize: '14px',
    fontWeight: '500',
    cursor: 'pointer',
    transition: 'all 0.2s',
  },
  backSection: {
    maxWidth: '1200px',
    margin: '0 auto',
    padding: '0 20px 40px',
  },
  backLink: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '8px',
    color: '#1a73e8',
    textDecoration: 'none',
    fontSize: '15px',
    fontWeight: '500',
    transition: 'all 0.2s',
  },
};

const styleSheet = document.createElement('style');
styleSheet.innerHTML = `
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }

  mark {
    background: #fff3cd;
    color: #202124;
    padding: 2px 0;
    border-radius: 2px;
  }

  @media (hover: hover) {
    input:focus {
      border-color: #1a73e8 !important;
      box-shadow: 0 0 0 3px rgba(26,115,232,0.1) !important;
    }

    button:hover {
      opacity: 0.9;
    }

    .searchBtn:hover {
      background: #1557b0 !important;
    }

    .clearBtn:hover {
      background: #f1f3f4 !important;
    }

    .categoryTab:hover {
      border-color: #1a73e8 !important;
      color: #1a73e8 !important;
    }

    .popularTag:hover {
      background: #f8f9fa !important;
      border-color: #1a73e8 !important;
    }

    .browseBtn:hover {
      background: #1557b0 !important;
      transform: translateY(-2px);
    }

    .backLink:hover {
      gap: 12px !important;
    }
  }

  @media (max-width: 768px) {
    h1 {
      font-size: 32px !important;
    }

    .searchBox {
      flex-direction: column;
    }

    .searchInput {
      padding-right: 56px !important;
    }

    .searchBtn {
      position: relative !important;
      right: auto !important;
      width: 100%;
    }

    .clearBtn {
      right: 20px !important;
    }

    .resultHeader {
      flex-direction: column;
      align-items: flex-start !important;
    }
  }
`;
document.head.appendChild(styleSheet);

export default HelpSearchPage;